import React from "react";
import { useParams } from "react-router-dom";
import {
  Target,
  CheckCircle,
  ShieldCheck,
  Zap,
  FileText,
  Cloud,
  Server,
  Database,
  GitBranch,
  BarChart,
  Network,
  HardDrive,
  Cpu,
  Lock,
  Monitor,
  Globe
} from "lucide-react";
import Header2 from "../../components/Header2/Header2";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import caseStudies from "../../api/caseStudies";

export default function CaseStudyDetailPage() {
  const { slug } = useParams();
  const caseStudy = caseStudies.find((cs) => cs.slug === slug);
  const isWideDesktop = window.innerWidth >= 1600;
  const containerMaxWidth = isWideDesktop ? '1760px' : '1200px';
  const contentMaxWidth = isWideDesktop ? '1680px' : '1400px';

  if (!caseStudy) {
    return (
      <div className="body_wrap sco_agency">
        <Header2 />
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          color: '#1a202c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          Case Study Not Found
        </div>
        <Footer />
        <Scrollbar />
      </div>
    );
  }

  const getServiceIcon = (serviceName) => {
    if (serviceName.includes("AWS") || serviceName.includes("Cloud")) return <Cloud size={24} />;
    if (serviceName.includes("Database") || serviceName.includes("RDS")) return <Database size={24} />;
    if (serviceName.includes("Compute") || serviceName.includes("ECS")) return <Server size={24} />;
    if (serviceName.includes("CI/CD") || serviceName.includes("GitHub Actions") || serviceName.includes("Jenkins")) return <GitBranch size={24} />;
    if (serviceName.includes("Monitoring") || serviceName.includes("CloudWatch") || serviceName.includes("Prometheus")) return <Monitor size={24} />;
    if (serviceName.includes("Networking") || serviceName.includes("Route53") || serviceName.includes("VPC")) return <Network size={24} />;
    if (serviceName.includes("Security") || serviceName.includes("IAM") || serviceName.includes("Secrets Manager")) return <Lock size={24} />;
    if (serviceName.includes("Storage") || serviceName.includes("S3") || serviceName.includes("EFS")) return <HardDrive size={24} />;
    if (serviceName.includes("Kubernetes") || serviceName.includes("EKS")) return <Cpu size={24} />;
    if (serviceName.includes("Edge") || serviceName.includes("CloudFront")) return <Globe size={24} />;
    return <Target size={24} />; // Default icon
  };

  return (
    <>
      <div className="body_wrap sco_agency">
        <Header2 />
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          color: '#1a202c',
        }}>
          {/* Container */}
          <div style={{
            maxWidth: containerMaxWidth,
            margin: '0 auto',
            padding: isWideDesktop ? '60px 16px' : '60px 20px',
          }}>

            {/* ====== HEADING SECTION ====== */}
            <header style={{
              textAlign: 'center',
              marginBottom: '80px',
              padding: '0 20px',
              marginTop: "60px"
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'rgba(189, 2, 29, 0.1)',
                color: '#bd021d',
                padding: '8px 16px',
                borderRadius: '20px',
                fontWeight: '600',
                fontSize: '14px',
                marginBottom: '24px',
                transform: 'translateY(0)',
                transition: 'all 0.3s ease',
                animation: 'fadeInUp 0.6s ease-out'
              }}>
                <Target size={16} /> Case Study
              </div>

              <h1 style={{
                fontSize: isWideDesktop ? '54px' : '48px',
                fontWeight: '800',
                lineHeight: '1.2',
                marginBottom: '20px',
                background: 'linear-gradient(135deg, #1a202c 0%, #bd021d 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'fadeInUp 0.8s ease-out 0.2s both'
              }}>
                {caseStudy.title}
              </h1>

              <p style={{
                fontSize: isWideDesktop ? '20px' : '18px',
                color: '#4a5568',
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: '1.6',
                animation: 'fadeInUp 0.8s ease-out 0.4s both'
              }}>
                {caseStudy.summary}
              </p>
            </header>

            <main style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '60px',
              maxWidth: contentMaxWidth,
              margin: '0 auto'
            }}>

              {/* ====== INTRODUCTION SECTION ====== */}
              <section id="introduction" style={{
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                padding: '40px',
                border: '1px solid #e2e8f0',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                animation: 'slideInLeft 0.8s ease-out'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #bd021d, #1a202c)',
                  transform: 'scaleX(0)',
                  transition: 'transform 0.3s ease',
                }} className="section-top-border"></div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#bd021d',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    transition: 'transform 0.3s ease'
                  }}>
                    <FileText size={20} />
                  </div>
                  <h2 style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: '#1a202c'
                  }}>Introduction</h2>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isWideDesktop ? '1.15fr 0.95fr' : '1fr 1fr',
                  gap: isWideDesktop ? '56px' : '40px',
                  alignItems: 'start'
                }}>
                  <div>
                    <p style={{
                      color: '#4a5568',
                      lineHeight: '1.7',
                      fontSize: '16px',
                      marginBottom: '20px'
                    }}>
                      {caseStudy.introduction}
                    </p>
                  </div>

                  {caseStudy.solutions.key_features && (
                    <div style={{
                      backgroundColor: 'white',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'linear-gradient(90deg, #bd021d, transparent)',
                        transform: 'translateX(-100%)',
                        transition: 'transform 0.5s ease'
                      }} className="highlight-bar"></div>
                      
                      <h4 style={{
                        fontWeight: '600',
                        color: '#1a202c',
                        marginBottom: '12px'
                      }}>Key Features</h4>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {caseStudy.solutions.key_features.map((item, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'transform 0.2s ease',
                            padding: '4px 0'
                          }}>
                            <CheckCircle size={16} style={{ 
                              color: '#bd021d', 
                              flexShrink: 0,
                              transition: 'transform 0.2s ease'
                            }} />
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#4a5568',
                              transition: 'color 0.2s ease'
                            }}>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* ====== CHALLENGES SECTION ====== */}
              <section id="challenges" style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '40px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                animation: 'slideInRight 0.8s ease-out'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #1a202c, #bd021d)',
                  transform: 'scaleX(0)',
                  transition: 'transform 0.3s ease',
                }} className="section-top-border"></div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#bd021d',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    transition: 'transform 0.3s ease'
                  }}>
                    <Zap size={20} />
                  </div>
                  <h2 style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: '#1a202c'
                  }}>Challenges</h2>
                </div>

                <div style={{ color: '#4a5568', lineHeight: 1.8, fontSize: '16px' }}>
                  <p style={{ marginBottom: '12px' }}>
                    The client faced several operational and technical challenges that hindered
                    their ability to serve a growing global customer base. Primary issues included:
                  </p>

                  <ol style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                    {caseStudy.challenges.map((challenge, index) => (
                      <li key={index}>{challenge}</li>
                    ))}
                  </ol>
                </div>
              </section>

              {/* ====== OUR SOLUTIONS SECTION ====== */}
              <section id="solutions" style={{
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                padding: '40px',
                border: '1px solid #e2e8f0',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                animation: 'slideInLeft 0.8s ease-out'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #bd021d, #1a202c)',
                  transform: 'scaleX(0)',
                  transition: 'transform 0.3s ease',
                }} className="section-top-border"></div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#bd021d',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    transition: 'transform 0.3s ease'
                  }}>
                    <ShieldCheck size={20} />
                  </div>
                  <h2 style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: '#1a202c'
                  }}>Our Solutions</h2>
                </div>

               <div style={{ color: '#4a5568', lineHeight: 1.8, fontSize: '16px' }}>
  {Object.entries(caseStudy.solutions)
    .filter(([key]) => key !== 'key_features') // 👈 EXCLUDE THIS
    .map(([key, value]) => (
      <p key={key} style={{ marginBottom: '10px' }}>
        <strong>
          {key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase())}
          :
        </strong>{" "}
        {Array.isArray(value) ? value.join(', ') : value}
      </p>
    ))}
</div>

              </section>

              {/* ====== BENEFITS SECTION ====== */}
              <section id="benefits" style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '40px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                animation: 'slideInRight 0.8s ease-out'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #1a202c, #bd021d)',
                  transform: 'scaleX(0)',
                  transition: 'transform 0.3s ease',
                }} className="section-top-border"></div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#bd021d',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    transition: 'transform 0.3s ease'
                  }}>
                    <CheckCircle size={20} />
                  </div>
                  <h2 style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: '#1a202c'
                  }}>Benefits</h2>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px'
                }}>
                  {caseStudy.benefits.map((benefit, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      transition: 'all 0.3s ease',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      // cursor: 'pointer'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        backgroundColor: '#bd021d',
                        borderRadius: '50%',
                        marginTop: '2px',
                        flexShrink: 0,
                        transition: 'all 0.3s ease',
                        position: 'relative'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '0px',
                          height: '0px',
                          backgroundColor: 'rgba(189, 2, 29, 0.2)',
                          borderRadius: '50%',
                          transition: 'all 0.3s ease'
                        }} className="benefit-pulse"></div>
                      </div>
                      <span style={{
                        color: '#4a5568',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        transition: 'color 0.2s ease'
                      }}>{benefit}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* ====== SERVICES SECTION ====== */}
              <section id="services" style={{
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                padding: '40px',
                border: '1px solid #e2e8f0',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                animation: 'slideInLeft 0.8s ease-out'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #bd021d, #1a202c)',
                  transform: 'scaleX(0)',
                  transition: 'transform 0.3s ease',
                }} className="section-top-border"></div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#bd021d',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    transition: 'transform 0.3s ease'
                  }}>
                    <BarChart size={20} />
                  </div>
                  <h2 style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: '#1a202c'
                  }}>Services & Technology</h2>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '24px'
                }}>
                  {caseStudy.services.map((service, index) => (
                    <div key={index} style={{
                      backgroundColor: 'white',
                      padding: '24px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      // cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #bd021d, #1a202c)',
                        transform: 'scaleX(0)',
                        transition: 'transform 0.3s ease'
                      }} className="card-top-border"></div>
                      
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(189, 2, 29, 0.03), transparent)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease'
                      }} className="service-hover-bg"></div>
                      
                      <div style={{
                        width: '56px',
                        height: '56px',
                        backgroundColor: 'rgba(189, 2, 29, 0.1)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#bd021d',
                        margin: '0 auto 16px auto',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        {getServiceIcon(service)}
                      </div>
                      <h3 style={{
                        fontWeight: '600',
                        color: '#1a202c',
                        marginBottom: '8px',
                        fontSize: '16px',
                        position: 'relative',
                        zIndex: 1
                      }}>{service.split(':')[0]}</h3>
                      <p style={{
                        color: '#4a5568',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        position: 'relative',
                        zIndex: 1
                      }}>{service.split(':')[1] || ''}</p>
                    </div>
                  ))}
                </div>
              </section>

            </main>
          </div>
        </div>
        <Footer />
        <Scrollbar />
      </div>

      <style jsx>{`
      /* =============================
   ✅ RESPONSIVE DESIGN
   ============================= */

/* Large Tablets (≤1200px) */
@media (max-width: 1200px) {
  header h1 {
    font-size: 40px !important;
  }

  section {
    padding: 32px !important;
  }

  #introduction .grid,
  #benefits .grid,
  #services .grid {
    gap: 24px !important;
  }
}

/* Medium Tablets (≤992px) */
@media (max-width: 992px) {
  header h1 {
    font-size: 36px !important;
  }

  header p {
    font-size: 16px !important;
  }

  section {
    padding: 28px !important;
  }

  /* Stack two-column sections vertically */
  #introduction > div:last-child,
  #benefits > div,
  #services > div {
    grid-template-columns: 1fr !important;
  }

  #introduction div[style*="grid-template-columns"],
  #benefits div[style*="grid-template-columns"],
  #services div[style*="grid-template-columns"] {
    grid-template-columns: 1fr !important;
  }
}

/* Tablets & Large Mobiles (≤768px) */
@media (max-width: 768px) {
  header {
    margin-bottom: 50px !important;
  }

  header h1 {
    font-size: 30px !important;
    line-height: 1.3 !important;
  }

  header p {
    font-size: 15px !important;
  }

  section {
    padding: 24px !important;
  }

  /* Stack all two-column grids vertically */
  #introduction > div[style*="grid-template-columns"],
  #benefits > div[style*="grid-template-columns"],
  #services > div[style*="grid-template-columns"] {
    grid-template-columns: 1fr !important;
    gap: 20px !important;
  }

  /* Reduce benefit/service card padding */
  #services div[style*="padding: 24px"],
  #benefits div[style*="padding: 8px 12px"] {
    padding: 16px !important;
  }

  /* Reduce service icons */
  #services div[style*="width: 56px"] {
    width: 44px !important;
    height: 44px !important;
  }

  #services h3 {
    font-size: 15px !important;
  }

  #services p {
    font-size: 13px !important;
  }
}

/* Small Mobiles (≤576px) */
@media (max-width: 576px) {
  header h1 {
    font-size: 26px !important;
  }

  header p {
    font-size: 14px !important;
    line-height: 1.6 !important;
    padding: 0 10px !important;
  }

  section {
    padding: 20px !important;
    border-radius: 12px !important;
  }

  /* Collapse grids completely */
  #introduction div[style*="grid-template-columns"],
  #benefits div[style*="grid-template-columns"],
  #services div[style*="grid-template-columns"] {
    grid-template-columns: 1fr !important;
  }

  /* Reduce gap between sections */
  main {
    gap: 40px !important;
  }

  /* Adjust icon box */
  section div[style*="width: 40px"][style*="height: 40px"] {
    width: 32px !important;
    height: 32px !important;
  }

  section h2 {
    font-size: 22px !important;
  }

  section p,
  section li,
  section span {
    font-size: 14px !important;
  }
}

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Hover effects for sections */
        #introduction:hover .section-top-border,
        #challenges:hover .section-top-border,
        #solutions:hover .section-top-border,
        #benefits:hover .section-top-border,
        #services:hover .section-top-border {
          transform: scaleX(1);
        }

      `}</style>
    </>
  );
}
